import { useEffect, useRef, useState } from 'react'
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import { useBlocker, useNavigate } from '@tanstack/react-router'
import { Alert, App, Button, Form, Input, Skeleton, Switch, Tabs, Tag } from 'antd'
import { createSchemaFieldRule } from 'antd-zod'
import { ArrowLeft, LockKeyhole, ShieldCheck, Users } from 'lucide-react'
import { z } from 'zod'
import {
  createRoleSchema,
  permissionByCode,
  permissionDefinitions,
  type PermissionCode
} from '@super-mega/server/contracts/authorization'
import { FormPageContainer } from '@/components/form/form-page-container.tsx'
import { FORM_ERROR_SCROLL_OPTIONS } from '@/const/form.ts'
import { useTRPC } from '@/trpc/react.ts'
import { PermissionPicker } from './permission-picker.tsx'
import { RoleAccounts } from './role-accounts.tsx'
import type { RoleDetail } from './types.ts'

type Values = {
  code: string
  name: string
  description?: string | null
  isEnabled: boolean
  permissionCodes: PermissionCode[]
}
const fieldRule = createSchemaFieldRule(createRoleSchema.omit({ catalogVersion: true }))

type EditorPageProps = {
  roleId?: string
  tab?: 'permissions' | 'accounts'
  onTabChange?: (tab: 'permissions' | 'accounts') => void
}

export function RoleEditorPage({ roleId, tab = 'permissions', onTabChange }: EditorPageProps) {
  const trpc = useTRPC()
  const catalog = useQuery({ ...trpc.authorization.listPermissions.queryOptions(), staleTime: 0 })
  const validId = z.uuid().safeParse(roleId).success
  const role = useQuery({
    ...trpc.authorization.roleDetail.queryOptions({ roleId: roleId ?? '' }),
    enabled: validId,
    staleTime: 0
  })
  if (roleId && !validId)
    return (
      <FormPageContainer title="角色配置">
        <Alert type="error" showIcon title="角色 ID 无效" />
      </FormPageContainer>
    )
  if ((catalog.isError && !catalog.data) || (role.isError && !role.data))
    return (
      <FormPageContainer title="角色配置">
        <Alert type="error" showIcon title={catalog.error?.message ?? role.error?.message ?? '无法载入配置'} />
      </FormPageContainer>
    )
  if (!catalog.data || (roleId && !role.data))
    return (
      <FormPageContainer title="角色配置">
        <Skeleton active className="bg-surface rounded-lg p-6" />
      </FormPageContainer>
    )
  const catalogOutdated = JSON.stringify(catalog.data.definitions) !== JSON.stringify(permissionDefinitions)
  return (
    <>
      {(catalog.isError || role.isError) && (
        <Alert
          className="mb-4"
          type="error"
          showIcon
          title="刷新配置失败，当前草稿仍保留"
          description={catalog.error?.message ?? role.error?.message}
        />
      )}
      {catalogOutdated && (
        <Alert className="mb-4" type="warning" showIcon title="权限目录已更新，请刷新应用后重新编辑；当前草稿仍保留" />
      )}
      <RoleEditor
        key={roleId ?? 'new'}
        role={role.data}
        tab={tab}
        onTabChange={onTabChange}
        catalogVersion={catalog.data.catalogVersion}
        catalogOutdated={catalogOutdated}
      />
    </>
  )
}

function RoleEditor({
  role,
  catalogVersion,
  catalogOutdated,
  tab,
  onTabChange
}: {
  role?: RoleDetail
  catalogVersion: string
  catalogOutdated: boolean
  tab: 'permissions' | 'accounts'
  onTabChange?: EditorPageProps['onTabChange']
}) {
  const trpc = useTRPC()
  const cache = useQueryClient()
  const navigate = useNavigate()
  const { modal, message } = App.useApp()
  const [form] = Form.useForm<Values>()
  const [initial] = useState(() => ({
    version: role?.version,
    catalogVersion,
    values: {
      code: role?.code ?? '',
      name: role?.name ?? '',
      description: role?.description ?? '',
      isEnabled: role?.isEnabled ?? true,
      permissionCodes: role?.permissionCodes ?? []
    } satisfies Values
  }))
  const [dirty, setDirty] = useState(false)
  const [error, setError] = useState<string>()
  const allowNavigation = useRef(false)
  const create = useMutation(trpc.authorization.createRole.mutationOptions())
  const update = useMutation(trpc.authorization.updateRole.mutationOptions())
  const busy = create.isPending || update.isPending
  const protectedRole = role?.isProtected ?? false
  const blocker = useBlocker({
    shouldBlockFn: ({ current, next }) =>
      current.pathname !== next.pathname && dirty && !allowNavigation.current && !protectedRole,
    enableBeforeUnload: () => dirty && !allowNavigation.current,
    withResolver: true
  })
  useEffect(() => {
    if (blocker.status !== 'blocked') return
    const dialog = modal.confirm({
      title: '权限配置尚未保存',
      content: '离开会丢失当前修改。',
      okText: '离开页面',
      cancelText: '继续编辑',
      onOk: () => {
        // 列表会恢复查询参数并再次跳转；确认丢弃后应允许整个离开流程。
        allowNavigation.current = true
        blocker.proceed()
      },
      onCancel: () => blocker.reset()
    })
    return () => dialog.destroy()
  }, [blocker, modal])
  const back = () => navigate({ to: '/system-management/roles', search: {} })
  const submit = async (values: Values) => {
    if (catalogOutdated) return
    const previous = new Set(initial.values.permissionCodes)
    const selected = new Set(values.permissionCodes)
    const added = values.permissionCodes.filter((code) => !previous.has(code))
    const removed = initial.values.permissionCodes.filter((code) => !selected.has(code))
    modal.confirm({
      title: role ? '确认保存角色配置' : '确认创建角色',
      okText: '确认保存',
      cancelText: '返回修改',
      content: (
        <div className="space-y-3">
          <p>
            影响 {role?.accountCount ?? 0} 个关联账号，其中活跃账号 {role?.activeAccountCount ?? 0} 个。
          </p>
          <p>状态：{values.isEnabled ? '启用' : '停用'}。</p>
          <div>
            <Tag color="success">新增 {added.length}</Tag>
            {added.length > 0 && (
              <ul>
                {added.map((code) => (
                  <li key={code}>{permissionByCode.get(code)?.name}</li>
                ))}
              </ul>
            )}
          </div>
          <div>
            <Tag color="error">撤销 {removed.length}</Tag>
            {removed.length > 0 && (
              <ul>
                {removed.map((code) => (
                  <li key={code}>{permissionByCode.get(code)?.name}</li>
                ))}
              </ul>
            )}
          </div>
        </div>
      ),
      onOk: async () => {
        try {
          const common = {
            name: values.name,
            description: values.description?.trim() || null,
            isEnabled: values.isEnabled,
            permissionCodes: values.permissionCodes,
            catalogVersion: initial.catalogVersion
          }
          if (role) await update.mutateAsync({ ...common, roleId: role.id, expectedVersion: initial.version! })
          else await create.mutateAsync(createRoleSchema.parse({ ...common, code: values.code }))
          await Promise.all([
            cache.invalidateQueries(trpc.authorization.listRoles.queryFilter()),
            cache.invalidateQueries(trpc.authorization.roleOptions.queryFilter()),
            cache.invalidateQueries(trpc.authorization.roleDetail.queryFilter()),
            cache.invalidateQueries(trpc.account.list.queryFilter()),
            cache.invalidateQueries(trpc.auth.session.queryFilter())
          ])
          allowNavigation.current = true
          setDirty(false)
          void message.success('角色配置已保存')
          await back()
        } catch (cause) {
          const text = cause instanceof Error ? cause.message : '保存失败'
          setError(text)
          void message.error(text)
          throw cause
        }
      }
    })
  }
  const permissionForm = (
    <Form<Values>
      form={form}
      name="role-permission-editor"
      initialValues={initial.values}
      layout="vertical"
      className="min-w-0"
      disabled={busy || protectedRole || catalogOutdated}
      onFinish={(values) => void submit(values)}
      onValuesChange={() => {
        allowNavigation.current = false
        setDirty(true)
      }}
      onFinishFailed={({ errorFields }) => {
        if (errorFields[0]) form.scrollToField(errorFields[0].name, FORM_ERROR_SCROLL_OPTIONS)
      }}
    >
      {!protectedRole && (
        <section className="border-border mb-5 border-b pb-2" aria-label="基本信息">
          <h2 className="text-content mb-4 text-base font-semibold">基本信息</h2>
          <div className="grid gap-x-6 sm:grid-cols-2 lg:grid-cols-[1fr_1fr_auto]">
            <Form.Item label="角色名称" name="name" rules={[fieldRule]} required>
              <Input maxLength={80} placeholder="请输入角色名称" />
            </Form.Item>
            <Form.Item
              label="角色编码"
              name="code"
              rules={[fieldRule]}
              required
              tooltip="小写字母、数字和下划线；创建后不可修改。"
            >
              <Input disabled={!!role || busy || catalogOutdated} placeholder="例如 finance_reader" />
            </Form.Item>
            <Form.Item label="状态" name="isEnabled" valuePropName="checked">
              <Switch checkedChildren="启用" unCheckedChildren="停用" />
            </Form.Item>
          </div>
          <Form.Item name="description" label="角色说明">
            <Input.TextArea
              maxLength={500}
              showCount
              autoSize={{ minRows: 1, maxRows: 3 }}
              placeholder="描述角色的职责与适用范围（选填）"
            />
          </Form.Item>
        </section>
      )}
      <Form.Item name="permissionCodes" className="mb-0!">
        <PermissionPicker disabled={busy || protectedRole || catalogOutdated} protectedRole={protectedRole} />
      </Form.Item>
    </Form>
  )
  let statusText = '配置已载入'
  if (protectedRole) statusText = '系统保护配置'
  else if (dirty) statusText = '有未保存修改'
  return (
    <FormPageContainer
      title={role?.name ?? '新增角色'}
      subTitle={<span className="hidden sm:inline">角色权限</span>}
      breadcrumbRender={false}
      tags={
        protectedRole ? (
          <Tag className="hidden! sm:inline-flex!" icon={<LockKeyhole className="mr-1 inline size-3" />}>
            系统内置
          </Tag>
        ) : undefined
      }
    >
      {error && (
        <Alert
          className="mb-4"
          type="error"
          title={error}
          showIcon
          description="当前输入仍然保留。若其他人已修改角色或权限目录，请重新载入后比较并重新编辑。"
        />
      )}
      {role && (
        <div className="bg-surface border-border mb-5 flex flex-wrap items-center justify-between gap-5 rounded-lg border p-5">
          <div className="flex min-w-0 items-start gap-4">
            <span className="bg-accent/10 text-accent flex size-11 shrink-0 items-center justify-center rounded-lg">
              <ShieldCheck className="size-6" />
            </span>
            <div className="min-w-0">
              <div className="flex flex-wrap items-center gap-2">
                <span className="text-content font-medium">{role.name}</span>
                <Tag color={role.isEnabled ? 'success' : 'default'}>{role.isEnabled ? '已启用' : '已停用'}</Tag>
                <code className="text-content-soft text-xs break-all">{role.code}</code>
              </div>
              <p className="text-content-muted mt-1.5 mb-0 text-sm leading-6">
                {protectedRole
                  ? '拥有系统全部权限，由系统维护，不支持修改或停用。'
                  : role.description || '配置该角色可访问的页面与业务操作。'}
              </p>
            </div>
          </div>
          <div className="border-border flex shrink-0 gap-6 sm:border-l sm:pl-6">
            <div>
              <span className="text-content text-xl font-semibold tabular-nums">{role.permissionCodes.length}</span>
              <span className="text-content-soft ml-2 text-xs">已授予权限</span>
            </div>
            <div>
              <span className="text-content text-xl font-semibold tabular-nums">{role.accountCount}</span>
              <span className="text-content-soft ml-2 text-xs">关联账号</span>
            </div>
          </div>
        </div>
      )}
      <div className="bg-surface border-border rounded-lg border p-4 sm:p-6">
        <Tabs
          activeKey={tab}
          onChange={(key) => onTabChange?.(key as 'permissions' | 'accounts')}
          items={[
            {
              key: 'permissions',
              label: '权限配置',
              icon: <ShieldCheck className="inline size-4" />,
              forceRender: true,
              children: permissionForm
            },
            ...(role
              ? [
                  {
                    key: 'accounts',
                    label: `关联账号 (${role.accountCount})`,
                    icon: <Users className="inline size-4" />,
                    children: <RoleAccounts roleId={role.id} />
                  }
                ]
              : [])
          ]}
        />
      </div>
      <div className="bg-surface border-border sticky bottom-0 z-10 mt-4 flex flex-wrap items-center justify-between gap-3 rounded-lg border px-4 py-3 sm:px-6">
        <span className="text-content-muted flex items-center gap-2 text-sm" role="status">
          {protectedRole && <LockKeyhole className="size-4" />}
          {statusText}
        </span>
        <div className="flex flex-wrap gap-2">
          <Button disabled={busy} icon={<ArrowLeft className="size-4" />} onClick={() => void back()}>
            返回列表
          </Button>
          {!protectedRole && (
            <>
              <Button
                disabled={busy || !dirty}
                onClick={() => {
                  form.resetFields()
                  setDirty(false)
                  setError(undefined)
                }}
              >
                恢复载入配置
              </Button>
              <Button
                type="primary"
                onClick={() => form.submit()}
                loading={busy}
                disabled={catalogOutdated || (!!role && !dirty)}
              >
                保存配置
              </Button>
            </>
          )}
        </div>
      </div>
    </FormPageContainer>
  )
}
